// Word-level diff for prose. Returns an array of {type, text} segments
// so the renderer can highlight additions / deletions inline. Splits on
// word + whitespace tokens (separately) so paragraph structure is
// preserved in the output.
//
// Algorithm: classic LCS DP. O(n*m) time + space. Fine for typical
// prompt sizes (<1500 tokens). Not built for diffing entire codebases.

export type DiffSegment = { type: "equal" | "add" | "del"; text: string }

export function wordDiff(before: string, after: string): DiffSegment[] {
  const a = tokenize(before)
  const b = tokenize(after)
  if (a.length === 0 && b.length === 0) return []
  const lcs = lcsTable(a, b)
  return walk(a, b, lcs)
}

// Match either a run of whitespace OR a run of non-whitespace as one
// token. Keeping whitespace tokens in the stream means we don't have
// to fabricate spaces between words at render time.
function tokenize(s: string): string[] {
  return s.match(/\s+|\S+/g) ?? []
}

function lcsTable(a: string[], b: string[]): Uint32Array {
  const n = a.length, m = b.length
  // Flat 2D table, (n+1) * (m+1).
  const t = new Uint32Array((n + 1) * (m + 1))
  for (let i = 1; i <= n; i++) {
    for (let j = 1; j <= m; j++) {
      if (a[i - 1] === b[j - 1]) {
        t[i * (m + 1) + j] = t[(i - 1) * (m + 1) + (j - 1)] + 1
      } else {
        const up   = t[(i - 1) * (m + 1) + j]
        const left = t[i * (m + 1) + (j - 1)]
        t[i * (m + 1) + j] = up > left ? up : left
      }
    }
  }
  return t
}

function walk(a: string[], b: string[], t: Uint32Array): DiffSegment[] {
  const n = a.length, m = b.length
  const stride = m + 1
  const segs: DiffSegment[] = []
  let i = n, j = m

  // Walk back through the table, prepending segments. Coalesce adjacent
  // same-type segments so the output isn't one segment per token.
  function push(type: DiffSegment["type"], text: string) {
    const head = segs[0]
    if (head && head.type === type) head.text = text + head.text
    else segs.unshift({ type, text })
  }

  while (i > 0 && j > 0) {
    if (a[i - 1] === b[j - 1]) {
      push("equal", a[i - 1])
      i--; j--
    } else {
      const up   = t[(i - 1) * stride + j]
      const left = t[i * stride + (j - 1)]
      if (up >= left) {
        push("del", a[i - 1])
        i--
      } else {
        push("add", b[j - 1])
        j--
      }
    }
  }
  while (i > 0) { push("del", a[--i]) }
  while (j > 0) { push("add", b[--j]) }
  return segs
}
